/* ============================================================================
   הצפנת צילומי תעודות זהות — קצה לקצה.

   המפתח הפרטי קיים רק בדפדפן של בעל הנכס. השרת מחזיק:
     • את המפתח הציבורי, בגלוי (הוא ציבורי);
     • את המפתח הפרטי מוצפן בסיסמה, כמחרוזת שאין לו דרך לפתוח;
     • את הקובץ המוצפן ואת מפתח התוכן העטוף.

   מה זה מונע: גיבוי גנוב של המסד, דלף מה-bucket, טעות תפעולית, עובד סקרן,
   או צו על האחסון — לא חושפים צילום ת"ז.

   מה זה לא מונע: מפעיל שמשנה באופן פעיל את קוד ה-JavaScript שהוא מגיש.
   זו מגבלה של כל אפליקציית web מוצפנת ואין דרך לעקוף אותה. ההגנה כאן היא
   מפני גישה פסיבית לנתונים, לא מפני מפעיל זדוני.

   מבנה: מעטפה. כל קובץ מוצפן במפתח AES-256 אקראי משלו, ואותו מפתח נעטף
   ב-RSA-OAEP של החשבון. כך אפשר לתת גישה לקובץ בודד בלי לחשוף את השאר.
   ============================================================================ */

const enc = new TextEncoder();

/** WebCrypto זמין רק בהקשר מאובטח — https, או localhost בפיתוח. */
export const cryptoAvailable = () =>
  typeof crypto !== "undefined" && !!crypto.subtle;

const subtle = () => {
  if (!cryptoAvailable()) {
    throw new Error("הצפנה אינה זמינה בדפדפן הזה. נדרש חיבור מאובטח (https).");
  }
  return crypto.subtle;
};

/* ------------------------------------------------------------- קידודים --- */

export const toB64 = (buf) => {
  const b = new Uint8Array(buf);
  let s = "";
  // מקטעים, כי fromCharCode עם מערך ענק חורג ממגבלת הארגומנטים.
  for (let i = 0; i < b.length; i += 0x8000) {
    s += String.fromCharCode.apply(null, b.subarray(i, i + 0x8000));
  }
  return btoa(s);
};

export const fromB64 = (s) => {
  const bin = atob(s);
  const b = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
  return b;
};

/* --------------------------------------------------------- זוג מפתחות --- */

const RSA = {
  name: "RSA-OAEP",
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: "SHA-256",
};

/** זוג מפתחות לחשבון. נוצר פעם אחת, בדפדפן, ולא עוזב אותו בגלוי. */
export async function generateAccountKeypair() {
  return subtle().generateKey(RSA, true, ["encrypt", "decrypt"]);
}

export const exportPublicKey = async (key) =>
  toB64(await subtle().exportKey("spki", key));

export const importPublicKey = async (b64) =>
  subtle().importKey("spki", fromB64(b64), RSA, true, ["encrypt"]);

const importPrivateKey = async (pkcs8) =>
  subtle().importKey("pkcs8", pkcs8, RSA, true, ["decrypt"]);

/* ------------------------------------------------- גזירת מפתח מסיסמה --- */

/* 600,000 סיבובים — ההמלצה של OWASP ל-PBKDF2-SHA256. זה לוקח כרבע שנייה
   בדפדפן, וזה בדיוק העיכוב שהופך ניחוש סיסמאות בכוח גס ליקר. */
const PBKDF2_ROUNDS = 600_000;

async function deriveWrappingKey(secret, salt) {
  const base = await subtle().importKey("raw", enc.encode(secret), "PBKDF2", false, [
    "deriveKey",
  ]);
  return subtle().deriveKey(
    { name: "PBKDF2", salt, iterations: PBKDF2_ROUNDS, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** עוטף את המפתח הפרטי בסוד (סיסמה או קוד שחזור). */
async function sealPrivateKey(privateKey, secret) {
  const pkcs8 = await subtle().exportKey("pkcs8", privateKey);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const kek = await deriveWrappingKey(secret, salt);
  const sealed = await subtle().encrypt({ name: "AES-GCM", iv }, kek, pkcs8);
  return { blob: toB64(sealed), salt: toB64(salt), iv: toB64(iv) };
}

async function openPrivateKey({ blob, salt, iv }, secret) {
  const kek = await deriveWrappingKey(secret, fromB64(salt));
  const pkcs8 = await subtle().decrypt(
    { name: "AES-GCM", iv: fromB64(iv) },
    kek,
    fromB64(blob),
  );
  return importPrivateKey(pkcs8);
}

/* ------------------------------------------------------- קוד שחזור --- */

/* אם בעל הנכס שוכח את הסיסמה, המפתח הפרטי אבוד ואיתו כל הצילומים — השרת
   לא יכול לשחזר אותו, וזו כל הנקודה. קוד השחזור הוא העותק השני. */
export function generateRecoveryCode() {
  const b = crypto.getRandomValues(new Uint8Array(20));
  // Base32 בלי אותיות שקל לבלבל (I/O/1/0), בקבוצות של 4.
  const A = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (const x of b) out += A[x % 32];
  return out.match(/.{1,4}/g).join("-");
}

export const normalizeRecoveryCode = (s) =>
  String(s || "").toUpperCase().replace(/[^A-Z2-9]/g, "");

/* --------------------------------------------- תעודת הזהות של בעל הנכס --- */

/* הסוד שפותח את הכספת הוא תעודת הזהות של בעל הנכס — אותו מזהה שמאשר
   מחיקה ושחזור. ספרות בלבד, כדי ש"012345678", "01-234567-8" ורווחים
   ייגזרו לאותו מפתח. שימו לב למחיר: ת"ז היא סוד חלש מסיסמה טובה, אבל
   היא תמיד זמינה לבעל הנכס — וזו הבחירה המוצהרת של המוצר (25.8.2026). */
export const normalizeOwnerId = (s) => String(s || "").replace(/\D/g, "");

/**
 * הקמה עם תעודת זהות: זוג מפתחות ארוז פעמיים — בת"ז ובקוד שחזור.
 * מחזיר את מה שנשמר בשרת ואת קוד השחזור להצגה חד-פעמית.
 */
export async function createKeyMaterialOwnerId(ownerId) {
  const pair = await generateAccountKeypair();
  const recoveryCode = generateRecoveryCode();

  const [byOwnerId, byRecovery, publicKey] = await Promise.all([
    sealPrivateKey(pair.privateKey, normalizeOwnerId(ownerId)),
    sealPrivateKey(pair.privateKey, normalizeRecoveryCode(recoveryCode)),
    exportPublicKey(pair.publicKey),
  ]);

  return {
    recoveryCode,
    privateKey: pair.privateKey,
    stored: { publicKey, byOwnerId, byRecovery },
  };
}

export async function unlockWithOwnerId(stored, ownerId) {
  try {
    return await openPrivateKey(stored.byOwnerId, normalizeOwnerId(ownerId));
  } catch {
    throw new Error("תעודת הזהות שגויה.");
  }
}

/** עוטף את המפתח הפרטי בת"ז — להוספת העטיפה לכספת ישנה של סיסמה. */
export async function sealWithOwnerId(privateKey, ownerId) {
  return sealPrivateKey(privateKey, normalizeOwnerId(ownerId));
}

/* --------------------------------------------------------- הקמת חשבון --- */

/**
 * המסלול הישן — הקמה עם סיסמה. נשאר לתאימות (כספות קיימות), ההקמה
 * החדשה היא createKeyMaterialOwnerId.
 */
export async function createKeyMaterial(password) {
  const pair = await generateAccountKeypair();
  const recoveryCode = generateRecoveryCode();

  const [byPassword, byRecovery, publicKey] = await Promise.all([
    sealPrivateKey(pair.privateKey, password),
    sealPrivateKey(pair.privateKey, normalizeRecoveryCode(recoveryCode)),
    exportPublicKey(pair.publicKey),
  ]);

  return {
    recoveryCode,
    privateKey: pair.privateKey,
    stored: { publicKey, byPassword, byRecovery },
  };
}

/** פותח את המפתח הפרטי בסיסמה. זורק אם הסיסמה שגויה. */
export async function unlockWithPassword(stored, password) {
  try {
    return await openPrivateKey(stored.byPassword, password);
  } catch {
    throw new Error("הסיסמה שגויה.");
  }
}

export async function unlockWithRecoveryCode(stored, code) {
  try {
    return await openPrivateKey(stored.byRecovery, normalizeRecoveryCode(code));
  } catch {
    throw new Error("קוד השחזור שגוי.");
  }
}

/** אורז מחדש את המפתח בסיסמה חדשה. נדרש בכל שינוי סיסמה. */
export async function resealWithPassword(privateKey, password) {
  return sealPrivateKey(privateKey, password);
}

/* --------------------------------------------------------- הצפנת קובץ --- */

/**
 * מצפין קובץ למפתח הציבורי של החשבון.
 * @returns {{ data: Blob, wrappedKey: string, iv: string }}
 */
export async function encryptForAccount(bytes, publicKeyB64) {
  const publicKey = await importPublicKey(publicKeyB64);

  const cek = await subtle().generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
  ]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await subtle().encrypt({ name: "AES-GCM", iv }, cek, bytes);

  const rawCek = await subtle().exportKey("raw", cek);
  const wrapped = await subtle().encrypt({ name: "RSA-OAEP" }, publicKey, rawCek);

  return {
    data: new Blob([ciphertext], { type: "application/octet-stream" }),
    wrappedKey: toB64(wrapped),
    iv: toB64(iv),
  };
}

/** מפענח קובץ. mime משמש רק לבניית ה-Blob להצגה. */
export async function decryptFile({ ciphertext, wrappedKey, iv }, privateKey, mime) {
  const rawCek = await subtle().decrypt(
    { name: "RSA-OAEP" },
    privateKey,
    fromB64(wrappedKey),
  );
  const cek = await subtle().importKey("raw", rawCek, { name: "AES-GCM" }, false, [
    "decrypt",
  ]);
  const plain = await subtle().decrypt(
    { name: "AES-GCM", iv: fromB64(iv) },
    cek,
    ciphertext,
  );
  return new Blob([plain], { type: mime || "image/jpeg" });
}

/** dataURL → בתים, לשימוש לפני הצפנה. */
export function dataUrlToBytes(dataUrl) {
  const i = String(dataUrl).indexOf(",");
  if (i < 0) throw new Error("פורמט תמונה לא נתמך");
  return fromB64(dataUrl.slice(i + 1));
}
